import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../layout/Navbar';
import '../../styles/tutor-profile.css';
import {
  FaStar,
  FaMapMarkerAlt,
  FaGraduationCap,
  FaClock,
  FaMoneyBill,
  FaCheckCircle,
  FaBook,
  FaUserGraduate,
  FaCalendarAlt,
  FaEnvelope,
  FaPhone,
  FaWhatsapp,
  FaExclamationTriangle,
  FaUser,
  FaAward,
  FaLanguage,
  FaChalkboardTeacher,
  FaHeart,
  FaRegHeart
} from 'react-icons/fa';

const TutorProfile = () => {
  const { id } = useParams();
  const { userData } = useAuth();
  const navigate = useNavigate();
  const [tutor, setTutor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    fetchTutorData();
  }, [id]);

  const fetchTutorData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch user data
      const userDoc = await getDoc(doc(db, 'users', id));
      
      if (!userDoc.exists()) {
        setError('Tutor profile not found');
        setLoading(false);
        return;
      }
      
      const userData = userDoc.data();
      
      // Fetch tutor-specific data
      const tutorDoc = await getDoc(doc(db, 'tutors', id));
      
      if (tutorDoc.exists()) {
        const tutorData = tutorDoc.data();
        setTutor({ ...userData, ...tutorData });
      } else {
        setTutor(userData);
      }
      
      // Fetch reviews
      try {
        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('tutorId', '==', id),
          where('status', '==', 'approved')
        );
        const reviewsSnapshot = await getDocs(reviewsQuery);
        const reviewsData = reviewsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setReviews(reviewsData);
      } catch (reviewError) {
        console.error('Error fetching reviews:', reviewError);
        setReviews([]);
      }
    } catch (error) {
      console.error('Error fetching tutor data:', error);
      setError('Failed to load tutor profile');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'TT';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getInitialColor = (name) => {
    const colors = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    ];
    const index = name?.length % colors.length || 0;
    return colors[index];
  };

  const renderAvailability = () => {
    if (!tutor?.availability || tutor.availability.length === 0) {
      return (
        <div className="empty-state">
          <FaCalendarAlt style={{ fontSize: '3rem', color: '#ddd', marginBottom: '20px' }} />
          <h4>No Availability Set</h4>
          <p>This tutor hasn't set their availability yet.</p>
        </div>
      );
    }

    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    
    return (
      <div className="availability-grid">
        {days.map(day => {
          const dayAvailability = Array.isArray(tutor.availability) 
            ? tutor.availability.find(a => {
                if (typeof a === 'string') return a.includes(day);
                if (a && typeof a === 'object') {
                  return a.day === day || a.day?.toLowerCase() === day.toLowerCase();
                }
                return false;
              })
            : null;
          
          return (
            <div key={day} className="availability-day">
              <h4>{day}</h4>
              {dayAvailability ? (
                <div className="time-slots">
                  {(() => {
                    if (typeof dayAvailability === 'string') {
                      return <span className="time-slot">{dayAvailability}</span>;
                    } else if (dayAvailability.time) {
                      return <span className="time-slot">{dayAvailability.time}</span>;
                    } else if (dayAvailability.start && dayAvailability.end) {
                      return (
                        <span className="time-slot">
                          {dayAvailability.start} - {dayAvailability.end}
                        </span>
                      );
                    } else {
                      return <span className="time-slot">Available</span>;
                    }
                  })()}
                </div>
              ) : (
                <p className="not-available">Not Available</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSubjects = () => {
    if (!tutor?.subjects || tutor.subjects.length === 0) {
      return (
        <div className="empty-state">
          <FaBook style={{ fontSize: '3rem', color: '#ddd', marginBottom: '20px' }} />
          <h4>No Subjects Listed</h4>
          <p>This tutor hasn't added any subjects yet.</p>
        </div>
      );
    }

    return (
      <div className="subjects-grid">
        {(Array.isArray(tutor.subjects) ? tutor.subjects : []).map((subject, index) => {
          const subjectName = typeof subject === 'object' ? subject.name || subject.subject : subject;
          const subjectRate = typeof subject === 'object' ? subject.rate : tutor.hourlyRate;
          const subjectLevel = typeof subject === 'object' ? subject.level : 'All Levels';
          
          return (
            <div key={index} className="subject-card" data-animate>
              <div className="subject-card-header">
                <FaBook className="subject-icon" />
                <h4>{subjectName || 'Subject'}</h4>
              </div>
              <div className="subject-card-body">
                <p className="subject-level">{subjectLevel}</p>
                <div className="subject-rate">
                  <span className="rate-amount">${subjectRate || tutor.hourlyRate || 0}</span>
                  <span className="rate-period">/hour</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading tutor profile...</p>
        </div>
      </>
    );
  }

  if (error || !tutor) {
    return (
      <>
        <Navbar />
        <div className="error-container">
          <FaExclamationTriangle className="error-icon" />
          <h2>{error || 'Tutor not found'}</h2>
          <p>The tutor profile you're looking for doesn't exist or cannot be loaded.</p>
          <button onClick={() => navigate('/search')} className="btn btn-primary">
            Browse Other Tutors
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      
     
      
      <div className="tutor-profile-page">
        {/* Hero Section */}
        <div className="tutor-hero">
          <div className="container">
            <div className="tutor-hero-content">
              <div className="tutor-avatar-large">
                {tutor.profileImage ? (
                  <img 
                    src={tutor.profileImage} 
                    alt={tutor.fullName}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `
                        <div style="
                          width: 150px;
                          height: 150px;
                          background: ${getInitialColor(tutor.fullName)};
                          border-radius: 50%;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          color: white;
                          font-size: 3rem;
                          font-weight: bold;
                        ">
                          ${getInitials(tutor.fullName)}
                        </div>
                      `;
                    }}
                  />
                ) : (
                  <div 
                    className="avatar-placeholder"
                    style={{ background: getInitialColor(tutor.fullName) }}
                  >
                    {getInitials(tutor.fullName)}
                  </div>
                )}
              </div>
              
              <div className="tutor-hero-info">
                <div className="tutor-header">
                  <h1>{tutor.fullName}</h1>
                  <button 
                    className="favorite-btn"
                    onClick={() => setIsFavorite(!isFavorite)}
                    aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                  >
                    {isFavorite ? <FaHeart /> : <FaRegHeart />}
                  </button>
                </div>
                
                <div className="tutor-badges">
                  {tutor.status === 'approved' && (
                    <span className="badge verified">
                      <FaCheckCircle /> Verified Tutor
                    </span>
                  )}
                  <span className="badge rating">
                    <FaStar /> {tutor.rating?.toFixed(1) || 'New'} 
                    <span className="review-count">({tutor.totalReviews || 0} reviews)</span>
                  </span>
                  <span className="badge sessions">
                    <FaClock /> {tutor.totalSessions || 0} sessions
                  </span>
                </div>
                
                <div className="tutor-meta">
                  {tutor.location && (
                    <div className="meta-item">
                      <FaMapMarkerAlt />
                      <span>{tutor.location}</span>
                    </div>
                  )}
                  {tutor.educationLevel && (
                    <div className="meta-item">
                      <FaGraduationCap />
                      <span>{tutor.educationLevel}</span>
                    </div>
                  )}
                  <div className="meta-item">
                    <FaMoneyBill />
                    <span className="rate">${tutor.hourlyRate || 0}/hour</span>
                  </div>
                </div>
                
                <p className="tutor-tagline">
                  {tutor.bio ? tutor.bio.substring(0, 120) + (tutor.bio.length > 120 ? '...' : '') : 'Passionate tutor ready to help you succeed.'}
                </p>
                
                <div className="tutor-actions">
                  <button 
                    className="btn btn-primary btn-lg"
                    onClick={() => navigate(`/book/${tutor.uid || tutor.id}`)}
                  >
                    <FaCalendarAlt /> Book a Session
                  </button>
                  
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="container">
          <div className="profile-tabs">
            <button 
              className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <FaUser className="tab-icon" /> Overview
            </button>
            <button 
              className={`tab ${activeTab === 'subjects' ? 'active' : ''}`}
              onClick={() => setActiveTab('subjects')}
            >
              <FaBook className="tab-icon" /> Subjects
            </button>
            <button 
              className={`tab ${activeTab === 'reviews' ? 'active' : ''}`}
              onClick={() => setActiveTab('reviews')}
            >
              <FaStar className="tab-icon" /> Reviews ({reviews.length})
            </button>
            <button 
              className={`tab ${activeTab === 'availability' ? 'active' : ''}`}
              onClick={() => setActiveTab('availability')}
            >
              <FaCalendarAlt className="tab-icon" /> Availability
            </button>
          </div>
          
          <div className="tab-content">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="overview-content">
                <div className="about-section">
                  <h2><FaUser /> About Me</h2>
                  <div className="bio-content">
                    {tutor.bio || 'No biography provided yet.'}
                  </div>
                </div>
                
                <div className="qualifications-grid">
                  {tutor.education && (
                    <div className="qual-card">
                      <FaGraduationCap className="qual-icon" />
                      <div>
                        <h3>Education</h3>
                        <p>{tutor.education}</p>
                      </div>
                    </div>
                  )}
                  
                  {tutor.experience && (
                    <div className="qual-card">
                      <FaAward className="qual-icon" />
                      <div>
                        <h3>Experience</h3>
                        <p>{tutor.experience} years of tutoring</p>
                      </div>
                    </div>
                  )}
                  
                  {tutor.languages && Array.isArray(tutor.languages) && tutor.languages.length > 0 && (
                    <div className="qual-card">
                      <FaLanguage className="qual-icon" />
                      <div>
                        <h3>Languages</h3>
                        <p>{tutor.languages.join(', ')}</p>
                      </div>
                    </div>
                  )}
                  
                  {tutor.teachingStyle && (
                    <div className="qual-card">
                      <FaChalkboardTeacher className="qual-icon" />
                      <div>
                        <h3>Teaching Style</h3>
                        <p>{tutor.teachingStyle}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {tutor.qualifications && Array.isArray(tutor.qualifications) && tutor.qualifications.length > 0 && (
                  <div className="qualifications-section">
                    <h2><FaAward /> Qualifications</h2>
                    <div className="qualifications-list">
                      {tutor.qualifications.map((qual, index) => (
                        <div key={index} className="qualification-item">
                          <FaCheckCircle />
                          <span>{qual}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Subjects Tab */}
            {activeTab === 'subjects' && (
              <div className="subjects-content">
                <div className="section-header">
                  <h2><FaBook /> Subjects & Rates</h2>
                  <p>Choose from the subjects I teach</p>
                </div>
                {renderSubjects()}
                
                <div className="rates-info">
                  <div className="info-card">
                    <h3><FaMoneyBill /> Rate Information</h3>
                    <ul>
                      <li>Minimum session duration: 1 hour</li>
                      <li>Group sessions: 20% discount per additional student</li>
                      <li>First session: 30% discount</li>
                      <li>Package deals: 10% off for 5+ sessions</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {/* Reviews Tab */}
            {activeTab === 'reviews' && (
              <div className="reviews-content">
                <div className="reviews-summary">
                  <div className="overall-rating">
                    <div className="rating-number">
                      {tutor.rating?.toFixed(1) || '0.0'}
                    </div>
                    <div className="stars-large">
                      {[...Array(5)].map((_, i) => (
                        <FaStar 
                          key={i}
                          className={i < Math.floor(tutor.rating || 0) ? 'filled' : 'empty'}
                        />
                      ))}
                    </div>
                    <p>{tutor.totalReviews || 0} reviews</p>
                  </div>
                </div>
                
                <div className="reviews-list">
                  {reviews.length > 0 ? (
                    reviews.map(review => (
                      <div key={review.id} className="review-card">
                        <div className="review-header">
                          <div className="reviewer-info">
                            <div className="reviewer-avatar">
                              {review.studentName?.charAt(0) || 'S'}
                            </div>
                            <div>
                              <h4>{review.studentName || 'Anonymous'}</h4>
                              <div className="review-rating">
                                {[...Array(5)].map((_, i) => (
                                  <FaStar 
                                    key={i}
                                    className={i < (review.rating || 0) ? 'filled' : 'empty'}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                          <span className="review-date">
                            {review.createdAt?.toDate().toLocaleDateString() || 'Recently'}
                          </span>
                        </div>
                        <div className="review-body">
                          <p>{review.comment || 'No comment provided.'}</p>
                        </div>
                        {review.subject && (
                          <div className="review-subject">
                            <FaBook /> {review.subject}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="empty-state">
                      <FaStar style={{ fontSize: '3rem', color: '#ddd', marginBottom: '20px' }} />
                      <h4>No Reviews Yet</h4>
                      <p>Be the first to review this tutor!</p>
                    </div>
                  )}
                </div>
                
                {userData?.userType === 'student' && (
                  <div className="write-review-section">
                    <button className="btn btn-primary">
                      Write a Review
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {/* Availability Tab */}
            {activeTab === 'availability' && (
              <div className="availability-content">
                <div className="section-header">
                  <h2><FaCalendarAlt /> Availability</h2>
                  <p>Book sessions during available time slots</p>
                </div>
                {renderAvailability()}
                
                <div className="booking-info">
                  <div className="info-card">
                    <h3><FaClock /> Booking Information</h3>
                    <ul>
                      <li>Book at least 24 hours in advance</li>
                      <li>Cancellation: Free up to 12 hours before</li>
                      <li>Reschedule: Available up to 6 hours before</li>
                      <li>Sessions: Conducted online via Zoom or Google Meet</li>
                      <li>Payment: Required upon booking confirmation</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TutorProfile;